import _ from "underscore";
import { getNotificationController } from "../../utils/native/notificationController";
import { nativeSpeak } from "../../utils/native/nativeSpeak";
import { openExternalUrl } from "../../utils/native/OpenExternalUrl";
import { htmlEscape } from "../../utils/vanilla";
import { MobileActions } from "../../store/mobile/actions";
import { PageLevel } from "../../store/mobile/types";
import { IPromptAction } from "../../services/alerts";
import pageHTML from "./native.tmpl";
import { handleApi } from "../../utils/error";
import DebugPageView from "./debugPageView";

export default class NativeView extends DebugPageView {
    private readonly template = _.template(pageHTML);
    constructor (options) {
        super(options);
        this.storeUnsubscribe = this.app.observeStore(state => [state.mobile.charging, state.mobile.background], this.updateMobileStates.bind(this));
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #say": this.say,
            "click .charge": this.charge,
            "click .background": this.background,
            "click #notify": this.notify,
            "click #externalLinkOpen": this.openExternalLink,
            "click #checkPermission": this.hasPermission,
            "click #requestPermission": this.requestPermission,
            "click #cancelNotification": this.cancel,
            "click #getActiveNotification": this.getActiveNotification,
            "click #openPromptDialog": this.openPromptDialog,
            "click #openPromptDialogNoTranslation": this.openPromptDialogNoTranslation
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        let mobileState = this.app.store.getState().mobile;
        this.el.innerHTML = this.template({
            chargingStatus: mobileState.charging ? "On" : "Off",
            backgroundStatus: mobileState.background ? "On" : "Off"
        });

        this.app.store.dispatch(MobileActions.setPageState({
            title: "Native Debug",
            pageLevel: PageLevel.TWO
        }));

        return this;
    }

    updateMobileStates() {
        let mobileState = this.app.store.getState().mobile;
        (<HTMLDivElement>document.getElementById("chargingStatus")).textContent = mobileState.charging ? "On" : "Off";
        (<HTMLDivElement>document.getElementById("backgroundStatus")).textContent = mobileState.background ? "On" : "Off";
    }

    say () {
        let speechInputElement = <HTMLInputElement>document.getElementById("speechInput");
        let speechPhrase = speechInputElement.value;

        speechPhrase && nativeSpeak(speechPhrase);
    }

    charge(e) {
        let isCharge = e.currentTarget.getAttribute("data-charge") === "true";
        this.app.store.dispatch(MobileActions.setCharging(isCharge));
    }

    background(e) {
        let isBackground = e.currentTarget.getAttribute("data-background") === "true";
        this.app.store.dispatch(MobileActions.setBackground({ isBackground, code: null }));
    }

    hasPermission() {
        let permissionStatusElement = <HTMLInputElement>document.getElementById("permissionStatus");
        getNotificationController().hasNotificationPermission().then(
            result => permissionStatusElement.textContent = result,
            (error: Error) => permissionStatusElement.textContent = error.message
        );
    }

    requestPermission() {
        let permissionStatusElement = <HTMLInputElement>document.getElementById("permissionStatus");
        getNotificationController().requestNotificationPermission().then(
            result => permissionStatusElement.textContent = result,
            (error: Error) => permissionStatusElement.textContent = error.message
        );
    }

    notify () {
        let notificationIdElement = <HTMLInputElement>document.getElementById("notificationId");
        let titleInputElement = <HTMLInputElement>document.getElementById("notificationTitle");
        let messageInputElement = <HTMLInputElement>document.getElementById("notificationMessage");
        let delayInputElement = <HTMLInputElement>document.getElementById("notificationDelay");
        let notificationElement = <HTMLInputElement>document.getElementById("notificationContent");

        let notificationId = parseInt(notificationIdElement.value, 10);
        let title = htmlEscape(titleInputElement.value) || titleInputElement.placeholder;
        let message = htmlEscape(messageInputElement.value) || messageInputElement.placeholder;
        let delay = delayInputElement.value || "0";
        let delayInt = parseInt(delay, 10);

        delayInt = isNaN(delayInt) ? 1 : delayInt;

        if (!isNaN(notificationId)) {
            getNotificationController().updateNotification(message, title, notificationId).then(
                result  => notificationElement.textContent = JSON.stringify(result),
                (error: Error) => notificationElement.textContent = error.message
            );
        } else {
            setTimeout(() => {
                getNotificationController().nativeNotify(message, title)
                .then(result  => notificationElement.textContent = JSON.stringify(result))
                .catch((error: Error) => {
                    notificationElement.textContent = error.message;
                    handleApi(error);
                });
            }, delayInt * 1000);
        }
    }

    cancel() {
        let notificationElement = <HTMLInputElement>document.getElementById("notificationContent");
        let cancelNotificationIdElement = <HTMLInputElement>document.getElementById("cancelNotificationId");
        if (cancelNotificationIdElement.value) {
            getNotificationController().cancelNativeNotification(parseInt(cancelNotificationIdElement.value, 10))
            .then(result => notificationElement.textContent = JSON.stringify(result))
            .catch((error: Error) => notificationElement.textContent = error.message);
        }
    }

    getActiveNotification() {
        let activeNotificationElement = <HTMLInputElement>document.getElementById("activeNotification");
        getNotificationController().getActiveNotifications()
        .then(result => activeNotificationElement.textContent = JSON.stringify(result))
        .catch((error: Error) => activeNotificationElement.textContent = JSON.stringify(error.message));
    }

    openExternalLink() {
        let externalLinkHrefElement = <HTMLInputElement>document.getElementById("externalLinkHref");
        let externalLinkTargetElement = <HTMLInputElement>document.getElementById("externalLinkTarget");

        let hideLocation = <HTMLInputElement>document.getElementById("externalLinkHideUrl");

        let externalLink = htmlEscape(externalLinkHrefElement.value) || externalLinkHrefElement.placeholder;
        let target = htmlEscape(externalLinkTargetElement.value) as TExternalUrlOpenTarget || externalLinkTargetElement.placeholder;

        const options: IExternalUrlOpenOptions = {
            hideurlbar: hideLocation.checked
        };

        openExternalUrl(externalLink, target, options);
    }

    openPromptDialogImpl (translate = true) {
        let titleInputElement = <HTMLInputElement>document.getElementById("promptDialogTitle");
        let messageInputElement = <HTMLInputElement>document.getElementById("promptDialogMessage");
        let delayInputElement = <HTMLInputElement>document.getElementById("promptDialogDelay");
        let optionOneInputElement = <HTMLInputElement>document.getElementById("promptDialogOption1");
        let optionTwoInputElement = <HTMLInputElement>document.getElementById("promptDialogOption2");

        let title = htmlEscape(titleInputElement.value) || titleInputElement.placeholder;
        let message = htmlEscape(messageInputElement.value) || messageInputElement.placeholder;
        let optionOne = htmlEscape(optionOneInputElement.value);
        let optionTwo = htmlEscape(optionTwoInputElement.value);
        let delay = delayInputElement.value || "0";
        let delayInt = parseInt(delay, 10);

        delayInt = isNaN(delayInt) ? 0 : delayInt;

        let renderPromptResponse = (response: string) => {
            let el = <HTMLDivElement>document.getElementById("promptDialogSelectedOption");
            if (el) {
                el.textContent = htmlEscape(`${ response }`);
            }
        };

        let actions: IPromptAction[] = [];
        if (optionOne) {
            actions.push({id: "debug-option-one", name: optionOne, action: () => renderPromptResponse(optionOne)});
        }
        if (optionTwo) {
            actions.push({id: "debug-option-two", name: optionTwo, action: () => renderPromptResponse(optionTwo)});
        }

        setTimeout(() => {
            void this.app.alertsService.prompt(message, title, actions, translate);
        }, delayInt * 1000);
    }

    openPromptDialog () {
        this.openPromptDialogImpl(true);
    }

    openPromptDialogNoTranslation () {
        this.openPromptDialogImpl(false);
    }
}